/**
 * ATL-1445 stage 3, P5 — the live watcher generalised to the WRITE flag: what an organization-document
 * observation means for a running session's two planes, and the restart sequences it calls for.
 *
 * Design: `docs/technical/write-cutover-stage3-app-seam.md` §4 ("Watching live") and §14 (as built).
 * The read half is ATL-888/889's (`global/Firebase/orgReadCutoverWatch.ts`) and is reused, not copied:
 * the one Firestore listener there now hands each snapshot to the write plane built here.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE RULE
 *
 * ATL-888's rule, applied to both planes: restart when what a fresh start would choose differs from
 * what this session is serving. "What a fresh start would choose" is computed by the SAME functions
 * startup uses — `decideReadCutoverReload`'s disjunction for reads, `resolveOrgWritesMaster` +
 * `decideSessionWriteMode` (P4) for writes — so after a restart the two agree, with ONE stated
 * exception: startup also weighs what outbox v2 holds (`outboxV2`), which the live decision cannot
 * see. The drain-to-empty restarts only once v2 reads empty, so a fresh start that can list v2 agrees;
 * one whose listing fails or times out (`unknown`) pins `outbox-v2-*` again and the watcher will drain
 * (empty) and restart again — a loop for as long as the listing keeps failing while the drainer's
 * does not. Unreachable until a bundle is capable (only a capable bundle or a remembered `'supabase'`
 * probes, and the drain leaves `'firestore'` in the memory); P9 must close it (§14, open question 1). "What the session is serving"
 * is the consumer's own record: `sessionReadsSupabaseRef` for reads, the seam's pin
 * (`getSessionWriteModePin`) for writes — never a re-read of the flag (ATL-886).
 *
 * | Session → observed (server read)                     | Action                       | Sequence |
 * |------------------------------------------------------|------------------------------|----------|
 * | writes firestore → firestore (every organization today) | ATL-888's read decision, VERBATIM | ATL-888's |
 * | write mode firestore → refuse/supabase               | `write-cutover`              | capped: 5 s grace, pending-skip drain, ≤3 s in-flight drain, reload |
 * | write mode supabase/refuse → firestore               | `write-rollback`             | drain v2 to EMPTY (uncapped; trading continues, «Синхронизация» notice), then the capped restart; a held entry blocks it and pages |
 * | reads → firestore while the write mode stays non-firestore | `read-rollback-after-v2-drain` | the same drain-to-empty, capped at `ROW3_DRAIN_CAP_MS` (review 1), then the restart (one reload lands both planes) |
 * | the organization says writes=supabase, reads=firestore | never entered: the target mode is `refuse` (P4's single-master answer), and it is ALERTED | as its row |
 *
 * The first row is the hard requirement: for an organization whose writes stay Firestore-master, the
 * decision object IS `decideReadCutoverReload`'s (same object, same fields), and the watcher takes
 * exactly the ATL-888 path with it (`writeCutoverWatch.test.js`, the differential and the timer pin).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT NEVER CHANGES A WRITE MODE
 *
 *  - A cached snapshot, a listener error, a missing document: the read decision is `unresolved` and
 *    is returned as is — no reload of either plane (ATL-888's refusals, kept).
 *  - A write flag that is not SERVER-confirmed. P4's rules (`resolveOrgWritesMaster`): only a server
 *    read is the truth; a cached read may raise but never lower; an unreadable value keeps the memory.
 *    The watcher only ever acts on the truth, so a non-server write observation moves nothing (the
 *    write flag's provenance is P4's stricter one: only `metadata.fromCache === false` is a server
 *    read, where the read flag's is ATL-888's `fromCache !== true`).
 *  - An unpinned session (the background pin has not landed), or a pin for another organization: the
 *    write plane is DEFERRED and the read plane decides alone — exactly as today — and the watcher
 *    re-decides the write plane once the pin lands (`reevaluateWritePlane`). Acting before the pin
 *    would compare against the unpinned default and restart every Supabase-master till at every start.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE MEMORY (`writesMasterMemory`) ACROSS A RESTART
 *
 * The watcher remembers server truths so the NEXT start falls back on them when it cannot read:
 *  - `'supabase'` at once (raising is always safe: queue-only never makes a second master);
 *  - `'firestore'` at once only while the session is in Firestore mode (nothing undelivered can
 *    exist); otherwise ONLY when the drain-to-empty has seen v2 empty, right before the reload — the
 *    line §11 says P5 must hold across a reload. Startup holds the other half: it never pins
 *    Firestore mode, nor lowers the memory, while v2 holds this organization's entries
 *    (`decideSessionWriteMode`'s `outboxV2`).
 */
import type { DrainResult } from '../API/writesInFlight'
import type { OrganizationDocSnapshot } from '../Firebase/orgReadCutover'
import {
  decideReadCutoverReload,
  observeOrganizationSnapshot,
  performLiveRollback,
  LIVE_ROLLBACK_GRACE_MS,
  type DecideReadCutoverReloadParams,
  type LiveReloadDecision,
  type OrgFlagsObservation,
  type OrgWritePlaneAction,
  isWritePlaneTransition,
  type OrgWritePlaneTransition,
  type OrgWritePlaneWatch,
} from '../Firebase/orgReadCutoverWatch'
import type { PendingSkipRestartDrainResult } from '../Firebase/dualWrite'
import type { OutboxRollbackBlockedAlert } from './outboxAlerts'
import type { OutboxQueueStatus } from './outboxDrainer'
import type { SessionOutboxV2 } from './sessionOutboxV2'
import type { SessionWriteModePin } from './writeW'
import type { WriteWCapability } from './writeWCapability'
import {
  decideSessionWriteMode,
  resolveOrgWritesMaster,
  writesMasterOfField,
  type LastKnownWritesMaster,
  type WriteMode,
  type WriteModeReason,
  type WritesMaster,
  type WritesMasterObservation,
} from './writeMode'

// ============================================================================
// Observing the document
// ============================================================================

/**
 * Read one organization snapshot for both planes. The read half is `observeOrganizationSnapshot`,
 * unchanged. The write half is read only from a snapshot the read half accepted as a payload
 * (`status: 'flag'`), with P4's provenance (`metadata.fromCache === false` ⇒ server). Never throws.
 */
export const observeOrganizationFlags = (snapshot: OrganizationDocSnapshot | null | undefined): OrgFlagsObservation => {
  const reads = observeOrganizationSnapshot(snapshot)
  if (reads.status !== 'flag' || !snapshot) return { reads, writes: null }
  try {
    const data = snapshot.data?.()
    if (!data) return { reads, writes: { kind: 'unreadable', why: 'empty-document' } }
    const value = writesMasterOfField(data.writesMaster)
    if (!value) return { reads, writes: { kind: 'unreadable', why: 'invalid-value' } }
    const provenance = snapshot.metadata?.fromCache === false ? 'server' : 'cache'
    return { reads, writes: { kind: 'read', provenance, value } }
  } catch {
    return { reads, writes: { kind: 'unreadable', why: 'read-error' } }
  }
}

// ============================================================================
// The session's write plane
// ============================================================================

/** What the seam's pin says this session is doing with W writes. */
export type SessionWritePlane =
  | { status: 'unpinned' }
  | { status: 'pinned'; organizationId: string; mode: WriteMode; reason: WriteModeReason; writesMaster: WritesMaster }

export const sessionWritePlaneOf = (pin: SessionWriteModePin | null): SessionWritePlane =>
  pin
    ? {
        status: 'pinned',
        organizationId: pin.decision.organizationId,
        mode: pin.decision.mode,
        reason: pin.decision.reason,
        writesMaster: pin.decision.resolution.writesMaster,
      }
    : { status: 'unpinned' }

/**
 * What the device's memory holds now, as far as this session KNOWS: what the watcher has stored since,
 * else what startup recalled. Deliberately NOT what startup asked to remember (`resolution.remember`):
 * that write may have failed, or been withheld while v2 blocked it, and treating it as stored would
 * stop the watcher from ever writing the server's answer (review finding). The cost is at most one
 * redundant write of a value startup did store.
 */
export const lastKnownOf = (pin: SessionWriteModePin | null, watcherRemembered: WritesMaster | null): LastKnownWritesMaster => {
  if (watcherRemembered) return { status: 'known', value: watcherRemembered, observedAt: '' }
  if (!pin) return { status: 'none' }
  return pin.decision.resolution.lastKnown
}

// ============================================================================
// The decision
// ============================================================================

/** Conditions worth a person's attention. Stable strings. */
export type WritePlaneAlert =
  /** The restart lands in §4's invalid state, which P4 answers with `refuse`: never entered, alerted. */
  | 'writes-supabase-without-reads'

export interface WriteCutoverDecision extends OrgWritePlaneTransition {
  /** Row 1 only: the in-memory pending-skip queue is drained before the restart. */
  drainPendingSkips: boolean
  from: { readsSupabase: boolean; writeMode: WriteMode }
  /** `writeMode` null: the write flag was not server-confirmed; the write plane is not what moves. */
  to: { readsSupabase: boolean; writeMode: WriteMode | null; reason: WriteModeReason | null; writesMaster: WritesMaster | null }
  /** What the memory must hold after this transition, and when it may be written. */
  remember: { value: WritesMaster; when: 'now' | 'after-v2-empty' } | null
  alerts: WritePlaneAlert[]
}

export type OrgCutoverDecision = LiveReloadDecision | WriteCutoverDecision

export const isWriteCutoverDecision = (d: OrgCutoverDecision): d is WriteCutoverDecision => isWritePlaneTransition(d)

export interface DecideOrgCutoverParams extends Omit<DecideReadCutoverReloadParams, 'observation'> {
  observation: OrgFlagsObservation
  /** The watcher's organization (the pin must be for it). */
  organizationId: string
  session: SessionWritePlane
  lastKnown: LastKnownWritesMaster
  capability: WriteWCapability
}


/**
 * Decide one observation for both planes (the table in the module docblock). Pure and total.
 *
 * Returns `decideReadCutoverReload`'s own result object whenever the write plane does not move, so a
 * session whose writes stay Firestore-master is decided exactly as ATL-888 decides it.
 */
export const decideOrgCutoverTransition = ({
  sessionReadsSupabase,
  deviceOverride,
  localDev,
  observation,
  organizationId,
  session,
  lastKnown,
  capability,
}: DecideOrgCutoverParams): OrgCutoverDecision => {
  const read = decideReadCutoverReload({ sessionReadsSupabase, deviceOverride, localDev, observation: observation.reads })
  // ATL-888's refusals and its startup deferral bind both planes.
  if (read.action === 'unresolved' || read.action === 'deferred' || sessionReadsSupabase === null) return read
  // The write plane is deferred until the seam is pinned for this organization.
  if (session.status !== 'pinned' || session.organizationId !== organizationId) return read

  const nextReads = read.action === 'cutover' ? true : read.action === 'rollback' ? false : sessionReadsSupabase
  // Only a server-confirmed flag can move the write plane (P4: a server read is the truth).
  const resolution = observation.writes ? resolveOrgWritesMaster(observation.writes, lastKnown) : null
  const next =
    resolution && resolution.basis === 'server'
      ? decideSessionWriteMode({ organizationId, resolution, sessionReadsSupabase: nextReads, capability })
      : null
  const fromMode = session.mode
  const toMode = next ? next.mode : fromMode

  // THE DIFFERENTIAL DOMAIN: writes stay Firestore — ATL-888 decides, verbatim.
  if (fromMode === 'firestore' && toMode === 'firestore') return read

  const from = { readsSupabase: sessionReadsSupabase, writeMode: fromMode }
  const to = {
    readsSupabase: nextReads,
    writeMode: next ? next.mode : null,
    reason: next ? next.reason : null,
    writesMaster: next ? next.resolution.writesMaster : null,
  }
  const alerts: WritePlaneAlert[] = next && next.alerts.includes('writes-supabase-without-reads') ? ['writes-supabase-without-reads'] : []
  const make = (
    action: OrgWritePlaneAction,
    sequence: WriteCutoverDecision['sequence'],
    remember: WriteCutoverDecision['remember'],
  ): WriteCutoverDecision => ({
    action,
    reload: true,
    sequence,
    drainPendingSkips: action === 'write-cutover',
    // The ROW, not its target: re-observing a row with a slightly different target (a garbled write
    // value, a read flip) is the same transition, retargeted by the watcher, never a new one.
    key: action,
    from,
    to,
    remember,
    alerts,
  })

  // Row 1: leaving Firestore mode. The org is Supabase-master now (next is non-null here).
  if (fromMode === 'firestore') return make('write-cutover', 'capped', { value: 'supabase', when: 'now' })
  // Row 2: back to Firestore mode. Drain v2 to empty first; lower the memory only then.
  if (toMode === 'firestore') return make('write-rollback', 'drain-v2-to-empty', { value: 'firestore', when: 'after-v2-empty' })
  // Row 3: reads go back to Firestore while writes stay non-Firestore. Never onto Firestore reads
  // with v2 non-empty, so the same drain comes first.
  if (read.action === 'rollback') return make('read-rollback-after-v2-drain', 'drain-v2-to-empty', null)
  // Anything else keeps ATL-888's decision: a read CUTOVER (to Supabase reads) is safe to restart on
  // capped — v2 is durable and an in-flight send is replayed idempotently — and refuse ⇄ supabase
  // only ever moves with reads.
  return read
}

/**
 * The memory write a server observation calls for right now (the module docblock), or null.
 * `'firestore'` while the session is not in Firestore mode is the drain-to-empty's to write.
 */
export const memoryWriteFor = (
  writes: WritesMasterObservation | null,
  session: SessionWritePlane,
  lastKnown: LastKnownWritesMaster,
): WritesMaster | null => {
  if (!writes || writes.kind !== 'read' || writes.provenance !== 'server') return null
  const remembered = lastKnown.status === 'known' ? lastKnown.value : null
  if (writes.value === remembered) return null
  if (writes.value === 'supabase') return 'supabase'
  // 'firestore': only a pinned Firestore-mode session may lower at once. An unpinned session leaves it
  // to the startup pin (P4 remembers its own server read).
  return session.status === 'pinned' && session.mode === 'firestore' ? 'firestore' : null
}

/** The organization's flags as configured, independent of this session. */
export type OrgFlagsAlert = 'org-writes-supabase-without-reads' | 'org-writes-master-unreadable'

export const orgFlagsAlerts = (observation: OrgFlagsObservation): OrgFlagsAlert[] => {
  if (observation.reads.status !== 'flag' || !observation.writes) return []
  const { writes } = observation
  if (writes.kind === 'unreadable') return ['org-writes-master-unreadable']
  if (writes.provenance === 'server' && writes.value === 'supabase' && !observation.reads.cutover) return ['org-writes-supabase-without-reads']
  return []
}

// ============================================================================
// The drain-to-empty step
// ============================================================================

export type V2RollbackStep =
  | { kind: 'empty' }
  | { kind: 'blocked'; reason: Exclude<OutboxRollbackBlockedAlert['reason'], 'outbox-unavailable'> }
  | { kind: 'waiting'; why: 'unknown' | 'draining' | 'master-check' | 'other-tab' | 'escalated' }

/**
 * Where a drain-to-empty stands, from the drainer's status. Pure. A held entry, an unreadable record
 * or a foreign-install hold BLOCKS (only a person can move the head); everything else waits.
 */
export const decideV2RollbackStep = (status: OutboxQueueStatus | null): V2RollbackStep => {
  if (!status || !status.known) return { kind: 'waiting', why: 'unknown' }
  if (status.blocked > 0 || status.stoppedBy === 'blocked') return { kind: 'blocked', reason: 'unreadable-record' }
  if (status.foreignInstall > 0 || status.stoppedBy === 'foreign-install') return { kind: 'blocked', reason: 'foreign-install' }
  if (status.held > 0 || status.stoppedBy === 'held') return { kind: 'blocked', reason: 'held' }
  if (status.depth === 0) return { kind: 'empty' }
  if (status.paused === 'master-changed' || status.stoppedBy === 'master-changed') return { kind: 'waiting', why: 'master-check' }
  if (status.readOnly) return { kind: 'waiting', why: 'other-tab' }
  if (status.escalated) return { kind: 'waiting', why: 'escalated' }
  return { kind: 'waiting', why: 'draining' }
}

// ============================================================================
// Row 1: the capped write cutover
// ============================================================================

export interface PerformWriteCutoverParams {
  notify: () => void
  reload: () => void
  /** `drainPendingSkipsBeforeRestart` (dualWrite.ts). */
  drainPendingSkips: () => Promise<PendingSkipRestartDrainResult>
  /** The capped in-flight wait (`waitForWritesToDrain`, `LIVE_ROLLBACK_DRAIN_CAP_MS`). */
  awaitDrain: () => Promise<DrainResult>
  /** Reports pending-skip entries the restart will lose. */
  onPendingSkipsLost?: (result: PendingSkipRestartDrainResult) => void
  graceMs?: number
  scheduleTimeout?: (handler: () => void, ms: number) => unknown
  logger?: Pick<Console, 'error' | 'warn' | 'log'>
}

/**
 * Row 1 — `performLiveRollback`'s sequence (not a copy of it): notify, 5 s grace, then the
 * pending-skip drain, then the capped in-flight drain, then reload. Returns the cancel.
 */
export const performWriteCutover = ({
  notify,
  reload,
  drainPendingSkips,
  awaitDrain,
  onPendingSkipsLost,
  graceMs = LIVE_ROLLBACK_GRACE_MS,
  scheduleTimeout = setTimeout,
  logger = console,
}: PerformWriteCutoverParams): (() => void) =>
  performLiveRollback({
    notify,
    reload,
    graceMs,
    scheduleTimeout,
    logger,
    label: 'write-cutover',
    drainExpiredMessage:
      'restarting with writes STILL IN FLIGHT — the drain cap expired. Their Supabase mirrors may be ' +
      'lost without a rejection (ATL-889), on an organization that is now Supabase-master; reconcile it.',
    reloadFailedMessage:
      'THE RELOAD FAILED — this device is STILL WRITING FIRESTORE-FIRST on a Supabase-master ' +
      'organization and must be restarted by hand',
    awaitDrain: async () => {
      try {
        const skips = await drainPendingSkips()
        // Anything but a clean queue is reported: entries still queued, or a pass that was still
        // running at the cap (its entries live outside the queue, so `remaining` alone can read 0).
        if (skips.outcome !== 'empty' && skips.outcome !== 'drained') {
          logger.error(
            `📊 [write-cutover] restarting with ${skips.remaining} pending-skip mirror(s) NOT replayed (${skips.outcome}` +
              `${skips.settled ? '' : ', a replay pass still running'}) — Firestore has them, the new write master does ` +
              'not; reconcile this organization',
            { remainingLabels: skips.remainingLabels, gate: skips.gate, waitedMs: skips.waitedMs },
          )
          onPendingSkipsLost?.(skips)
        }
      } catch (error) {
        logger.error('[write-cutover] the pending-skip drain failed; restarting regardless', { error })
      }
      return awaitDrain()
    },
  })

// ============================================================================
// Rows 2 and 3: drain v2 to empty, then the capped restart
// ============================================================================

/**
 * How long to wait before retrying when the session's outbox cannot be opened, and how long a drain
 * may go without the queue ever being read before that is paged as `outbox-unavailable`.
 */
export const V2_ROLLBACK_RETRY_MS = 30_000
/** How long the final memory write may hold the restart. The memory write never rejects. */
export const V2_ROLLBACK_REMEMBER_TIMEOUT_MS = 2000
/**
 * Review 1, item 1 — the bound on a READ rollback's drain (row 3), from the moment it starts: after
 * this the restart happens whatever v2 holds, so ATL-888's guarantee "the kill switch always fires"
 * holds for a session whose writes are Supabase-master too. Worst case: this + the 5 s grace + the 3 s
 * in-flight cap ≈ 38 s.
 *
 * Why a cap is safe here and not for row 2: row 3 never lowers the memory, and the session it restarts
 * into is Supabase-master with Firestore reads — `refuse` (§4's invalid state), so no second master can
 * form; and that session looks at v2 after its pin and drains it (`decideStartupOutboxLook`
 * 'after-pin'), so nothing the cap leaves behind is stranded. All the wait buys is that the device's
 * own writes land before its reads move — staleness, not safety. A held entry or an unreadable store
 * must not turn that into a veto on the kill switch. 30 s covers a healthy online queue (one request in
 * flight, hundreds of entries), and is short next to an incident.
 */
export const ROW3_DRAIN_CAP_MS = 30_000

export interface PerformV2DrainThenRestartParams {
  action: OrgWritePlaneAction
  /** The session's outbox, started on demand (`ensureSessionOutboxV2`). May throw. */
  openOutbox: () => SessionOutboxV2
  /** «Синхронизация…»: trading continues into v2 while it drains. Shown again if a restart is called off. */
  notifySyncing: () => void
  /** The drain is blocked (shown once per reason; never for `drain-cap-expired`, whose restart notice says it). */
  notifyBlocked: (reason: Exclude<OutboxRollbackBlockedAlert['reason'], 'drain-cap-expired'>) => void
  /** The restart warning (as `performLiveRollback`'s `notify`). */
  notifyRestart: () => void
  reload: () => void
  /** Pages through the P3 alerting (`OutboxAlertSink.rollbackBlocked`), once per reason. */
  page: (alert: OutboxRollbackBlockedAlert) => void
  /**
   * Lowers the memory (the transition's `remember`, possibly retargeted since); called after v2 was
   * seen empty, right before the reload. Resolves to whether anything was written.
   */
  rememberDrained?: () => Promise<boolean>
  /**
   * Raises the memory back to `'supabase'` when a lowered memory is NOT followed by the reload (the
   * last check found v2 non-empty again, or the sequence was cancelled): the memory may say
   * Firestore only while nothing is left to deliver. Raising is always safe (P4).
   */
  rememberUndrained?: () => Promise<boolean>
  /** The capped in-flight wait. */
  awaitDrain: () => Promise<DrainResult>
  /**
   * Review 1, item 1: a bound on the drain (row 3, `ROW3_DRAIN_CAP_MS`); null or omitted = uncapped
   * (row 2: its restart lands writes on Firestore, which must never happen over undelivered master
   * writes). When it expires the restart proceeds whatever v2 holds, and it is paged.
   */
  capMs?: number | null
  /**
   * Whether the cap may fire NOW. A retarget can turn the pending drain into a write rollback (it will
   * lower the memory), which must not be capped; checked again every `retryMs` while false.
   */
  capApplies?: () => boolean
  /** The restart notice when the cap expired (v2 not drained). Defaults to `notifyRestart`. */
  notifyCappedRestart?: () => void
  graceMs?: number
  retryMs?: number
  rememberTimeoutMs?: number
  now?: () => number
  scheduleTimeout?: (handler: () => void, ms: number) => unknown
  cancelTimeout?: (handle: unknown) => void
  logger?: Pick<Console, 'error' | 'warn' | 'log'>
}

/**
 * Rows 2 and 3. Row 2 is uncapped on purpose: its restart lands this device's WRITES on Firestore, and
 * it must not land there with master writes still undelivered — they would be lost to the books, or
 * sit behind Firestore-first writes that depend on them. Row 3 (reads only; it lowers no memory) is
 * capped by `capMs` (`ROW3_DRAIN_CAP_MS`, review 1): its restart lands in `refuse`, which drains v2
 * itself, so a held entry must not be able to veto the read kill switch. So:
 *
 *  1. notify «Синхронизация»; trading continues (the session's write mode is unchanged until reload);
 *  2. drain v2 until its status reads EMPTY. A held entry, an unreadable record or a foreign-install
 *     hold BLOCKS: shown, paged once per reason, and waited out (an operator resolving it lets the
 *     drain continue). An outbox that cannot be opened, or a queue never read for `retryMs`, is paged
 *     as `outbox-unavailable`; it never counts as empty;
 *  3. the capped restart: notify, grace, then a FINAL drain pass (anything queued during the grace goes
 *     first — back to step 2, with the syncing notice shown again, if the queue is not empty), the
 *     capped in-flight wait, the memory write, and the reload in the same tick as the last emptiness
 *     check. A memory lowered without that reload following is raised back (`rememberUndrained`).
 *
 * Returns the cancel (a flag flipped back, the watcher unmounting). Cancellation is re-checked after
 * every await. Residual, stated: an append still in progress at the instant of the reload (a capable
 * bundle's writer) survives on disk; the next start finds it (`shouldProbeOutboxV2AtStartup` always
 * probes for a capable bundle on a Firestore-master organization), refuses to pin Firestore mode, and
 * drains it first.
 */
export const performV2DrainThenRestart = ({
  action,
  openOutbox,
  notifySyncing,
  notifyBlocked,
  notifyRestart,
  reload,
  page,
  rememberDrained,
  rememberUndrained,
  awaitDrain,
  capMs = null,
  capApplies = () => true,
  notifyCappedRestart,
  graceMs = LIVE_ROLLBACK_GRACE_MS,
  retryMs = V2_ROLLBACK_RETRY_MS,
  rememberTimeoutMs = V2_ROLLBACK_REMEMBER_TIMEOUT_MS,
  now = Date.now,
  scheduleTimeout = setTimeout,
  cancelTimeout = (handle: unknown) => clearTimeout(handle as Parameters<typeof clearTimeout>[0]),
  logger = console,
}: PerformV2DrainThenRestartParams): (() => void) => {
  const label = action
  let cancelled = false
  let phase: 'draining' | 'restarting' = 'draining'
  let outbox: SessionOutboxV2 | null = null
  let unsubscribe: (() => void) | null = null
  /** The memory was lowered and no reload has followed yet. */
  let lowered = false
  /** The row-3 cap fired: the restart proceeds whatever v2 holds (review 1, item 1) — while it still applies. */
  let capExpired = false
  /**
   * The cap fell due while a restart was already under way (v2 had emptied in time). It is NOT fired
   * then — no page, no notice, the final drain pass still runs — but if that restart is called off and
   * the drain resumes, it fires at once (review 2, item 2).
   */
  let capDue = false
  const timers = new Set<unknown>()
  const paged = new Set<string>()

  const guard = (what: string, fn: () => void): void => {
    try {
      fn()
    } catch (error) {
      logger.error(`[${label}] ${what} failed`, { error })
    }
  }
  const later = (fn: () => void, ms: number): void => {
    const handle = scheduleTimeout(() => {
      timers.delete(handle)
      if (!cancelled) fn()
    }, ms)
    timers.add(handle)
  }
  const raiseMemoryBack = (why: string): void => {
    if (!lowered) return
    lowered = false
    logger.warn(`[${label}] the memory was lowered but no reload followed (${why}); raising it back`)
    if (rememberUndrained) void rememberUndrained().catch(() => false)
  }
  const block = (reason: OutboxRollbackBlockedAlert['reason'], status: OutboxQueueStatus | null): void => {
    if (paged.has(reason)) return
    paged.add(reason)
    // Counts are null, not 0, when the queue could not be read: "nothing listed" must never read as
    // "the queue is empty".
    const known = status !== null && status.known
    logger.error(`📊 [${label}] the restart is BLOCKED (${reason}) — this device keeps its write master until outbox v2 drains`, {
      depth: known ? status.depth : null,
    })
    // An expired cap is not a stop: the capped restart's own notice tells the cashier (review 2, item 4).
    if (reason !== 'drain-cap-expired') guard('the blocked notice', () => notifyBlocked(reason))
    guard('the page', () =>
      page({
        action,
        reason,
        depth: known ? status.depth : null,
        held: known ? status.held : null,
        blocked: known ? status.blocked : null,
        foreignInstall: known ? status.foreignInstall : null,
        oldestAgeMs: known && status.oldestEnqueuedAt !== null ? Math.max(0, now() - status.oldestEnqueuedAt) : null,
      }),
    )
  }

  const onStatus = (status: OutboxQueueStatus): void => {
    if (cancelled || phase !== 'draining') return
    const step = decideV2RollbackStep(status)
    if (step.kind === 'blocked') {
      block(step.reason, status)
      return
    }
    if (step.kind === 'empty') {
      phase = 'restarting'
      unsubscribe?.()
      unsubscribe = null
      restart()
    }
  }

  const drainPhase = (): void => {
    if (cancelled) return
    phase = 'draining'
    if (!outbox) {
      try {
        outbox = openOutbox()
      } catch (error) {
        logger.error(`[${label}] the session's outbox v2 could not be opened; retrying`, { error })
        block('outbox-unavailable', null)
        // Re-tried only while still draining: a stale retry must never start a second sequence over a
        // restart the cap already began (retryMs == capMs; review 3, N1).
        later(() => {
          if (phase === 'draining') drainPhase()
        }, retryMs)
        return
      }
    }
    const handle = outbox
    unsubscribe?.()
    unsubscribe = handle.subscribe(onStatus)
    onStatus(handle.status())
    if (phase !== 'draining') return
    void handle.drain('manual')
    // The drainer opens its store per pass, so a store that fails to list leaves the status unread
    // (`known: false`) forever and nothing above would ever fire. That is as blocked as a held entry.
    later(() => {
      if (phase === 'draining' && !handle.status().known) block('outbox-unavailable', handle.status())
    }, retryMs)
  }

  const backToDraining = (why: string): void => {
    raiseMemoryBack(why)
    logger.warn(`[${label}] the restart is called off (${why}); draining outbox v2 again`)
    // The restart warning was shown: say that the till is not restarting after all.
    guard('the syncing notice', notifySyncing)
    drainPhase()
    // A cap that fell due during the called-off restart fires now (review 2, item 2).
    if (capDue && phase === 'draining') {
      capDue = false
      if (capApplies()) expireCap()
      else armCap(retryMs)
    }
  }

  /**
   * Whether an expired cap still governs this restart. It does only while it still APPLIES: a pending
   * read rollback retargeted into a write rollback after the cap fired must not restart capped over
   * undelivered master writes (review 2, item 1). Then the expiry is withdrawn and the cap re-armed,
   * so a retarget back into a pure read rollback is capped again.
   */
  const capHolds = (): boolean => {
    if (!capExpired) return false
    if (capApplies()) return true
    capExpired = false
    logger.warn(`[${label}] the drain was retargeted into one that lowers the memory: the expired cap no longer applies`)
    if (capMs !== null) armCap(retryMs)
    return false
  }

  /** Undrained entries do not hold back a capped restart; otherwise they send it back to draining. */
  const mustKeepDraining = (handle: SessionOutboxV2 | null): boolean =>
    !capHolds() && (handle === null || decideV2RollbackStep(handle.status()).kind !== 'empty')

  const restart = (): void => {
    guard('the restart notice', capHolds() && notifyCappedRestart ? notifyCappedRestart : notifyRestart)
    later(() => {
      void (async () => {
        const handle = outbox
        if (!handle && !capHolds()) return backToDraining('the outbox is not open')
        // Trading continued during the grace: whatever it queued goes before the restart — unless the
        // cap expired, when the restarted session drains it instead (a send can take a transport timeout).
        if (handle && !capHolds()) {
          try {
            await handle.drain('manual')
          } catch (error) {
            logger.error(`[${label}] the final drain pass failed`, { error })
          }
        }
        if (cancelled) return
        if (mustKeepDraining(handle)) return backToDraining('v2 is not empty after the grace')
        try {
          const result = await awaitDrain()
          if (!result.drained) {
            logger.warn(`📊 [${label}] restarting with writes STILL IN FLIGHT — the drain cap expired (ATL-889)`, {
              remaining: result.remaining,
              waitedMs: result.waitedMs,
            })
          }
        } catch (error) {
          logger.error(`[${label}] could not check in-flight writes; restarting regardless`, { error })
        }
        if (cancelled) return
        // Re-checked AFTER the in-flight wait: a cap withdrawn during it (a retarget into a write
        // rollback) must not lower the memory over a non-empty v2 (review 3, S1).
        if (mustKeepDraining(handle)) return backToDraining('v2 is not empty before the memory write')
        if (rememberDrained && !capHolds()) {
          let timer: unknown = null
          const outcome = await Promise.race<boolean | 'timeout'>([
            rememberDrained().catch(() => false),
            new Promise<'timeout'>((resolve) => {
              timer = scheduleTimeout(() => resolve('timeout'), rememberTimeoutMs)
            }),
          ])
          if (timer !== null) cancelTimeout(timer)
          // Written, or still in flight (it may land after the restart): either way the memory may now
          // say Firestore. `false` is a write that failed or had nothing to write: the memory is as it was.
          lowered = outcome !== false
          if (outcome === 'timeout') logger.warn(`[${label}] the memory write did not confirm before the restart`)
          if (cancelled) return raiseMemoryBack('cancelled during the memory write')
        }
        // The last check, in the same tick as the reload.
        if (mustKeepDraining(handle)) return backToDraining('v2 is not empty at the last check')
        lowered = false
        // The sequence is over: nothing it armed (the cap, a retry) may fire after the reload — not even
        // if the reload throws and this JS context lives on.
        for (const timer of timers) cancelTimeout(timer)
        timers.clear()
        try {
          reload()
        } catch (error) {
          logger.error(`📊 [${label}] THE RELOAD FAILED — this device must be restarted by hand`, { error })
        }
      })()
    }, graceMs)
  }

  /** The cap fires: page, then the restart goes ahead whatever v2 holds. Only ever called while draining. */
  const expireCap = (): void => {
    capExpired = true
    const status = outbox ? outbox.status() : null
    logger.error(
      `📊 [${label}] the drain did not finish within ${capMs} ms — restarting ANYWAY (the kill switch always fires); ` +
        'the restarted session drains what is left',
      { depth: status && status.known ? status.depth : null },
    )
    paged.delete('drain-cap-expired')
    block('drain-cap-expired', status)
    phase = 'restarting'
    unsubscribe?.()
    unsubscribe = null
    restart()
  }

  const armCap = (ms: number): void => {
    later(() => {
      if (!capApplies()) {
        armCap(retryMs)
        return
      }
      // v2 emptied in time and the restart is under way: nothing to cap (review 2, item 2). Kept due,
      // in case that restart is called off.
      if (phase !== 'draining') {
        capDue = true
        return
      }
      expireCap()
    }, ms)
  }

  guard('the syncing notice', notifySyncing)
  if (capMs !== null) armCap(capMs)
  drainPhase()

  return () => {
    cancelled = true
    unsubscribe?.()
    unsubscribe = null
    for (const handle of timers) cancelTimeout(handle)
    timers.clear()
    raiseMemoryBack('cancelled')
  }
}

// ============================================================================
// The watcher's write plane
// ============================================================================

/** The memory callbacks a drain-to-empty sequence is given (`performV2DrainThenRestart`). */
export interface DrainMemory {
  /** Writes what the drain is to leave behind (the current target, which a retarget may change). */
  rememberDrained: () => Promise<boolean>
  /**
   * Whether the drain will lower the memory (a write rollback, or a read rollback retargeted into
   * one). Such a drain must stay uncapped; a drain that lowers nothing (row 3) is capped.
   */
  lowersMemory: () => boolean
  /** Raises the memory back to `'supabase'` (a lowered memory not followed by the reload). */
  rememberUndrained: () => Promise<boolean>
}

export interface WritePlaneWatchDeps {
  organizationId: string
  readSessionPin: () => SessionWriteModePin | null
  capability: WriteWCapability
  /** Writes the memory; resolves to whether it was stored, never rejects (`writesMasterMemory.remember`). */
  remember: (value: WritesMaster) => Promise<boolean>
  /** Starts row 1's sequence; returns its cancel. */
  startWriteCutover: (decision: WriteCutoverDecision) => () => void
  /** Starts rows 2/3's sequence; returns its cancel. `memory.rememberDrained` must be called only once v2 is empty. */
  startV2DrainThenRestart: (decision: WriteCutoverDecision, memory: DrainMemory) => () => void
  /** Telemetry for a transition started, retargeted or cancelled, and for an organization alert. */
  report?: (event: WritePlaneEvent) => void
  logger?: Pick<Console, 'error' | 'warn' | 'log'>
}

export type WritePlaneEvent =
  | { type: 'transition-started'; decision: WriteCutoverDecision }
  | { type: 'transition-retargeted'; decision: WriteCutoverDecision; remember: WritesMaster | null }
  | { type: 'transition-cancelled'; key: string; why: string }
  | { type: 'org-flags-alert'; alerts: OrgFlagsAlert[] }

/**
 * The write plane the read watcher's one listener drives (`watchOrganizationReadCutover`'s
 * `writePlane`). It owns the memory overlay, the serialised memory writes and the pending drain's
 * memory target; the restarts are the caller's (`writeCutoverSession.ts` binds them to the app).
 */
export const createWritePlaneWatch = ({
  organizationId,
  readSessionPin,
  capability,
  remember,
  startWriteCutover,
  startV2DrainThenRestart,
  report,
  logger = console,
}: WritePlaneWatchDeps): OrgWritePlaneWatch<WriteCutoverDecision> => {
  /** The last value the watcher STORED (confirmed by the memory). */
  let watcherRemembered: WritesMaster | null = null
  /** The last value queued for storing: re-observing it while its write is in flight queues nothing. */
  let watcherIntended: WritesMaster | null = null
  /**
   * Values whose write FAILED this session. The observation path does not retry them: on a device
   * whose storage is broken every organization-document emission would otherwise try (and warn)
   * again. The next start tries again; a drain's own writes are always attempted.
   */
  const failedWrites = new Set<WritesMaster>()
  /** What the pending drain-to-empty is to leave in the memory (a retarget may change it). */
  let drainTarget: WritesMaster | null = null
  let lastAlerts = ''
  // Memory writes are serialised: two AsyncStorage writes in flight have no defined order.
  let chain: Promise<unknown> = Promise.resolve()
  const write = (value: WritesMaster): Promise<boolean> => {
    watcherIntended = value
    const next = chain.then(async () => {
      const stored = await remember(value).catch(() => false)
      if (stored) {
        watcherRemembered = value
        failedWrites.delete(value)
      } else {
        failedWrites.add(value)
        // A failed write leaves the memory as it was.
        if (watcherIntended === value) watcherIntended = watcherRemembered
      }
      return stored
    })
    chain = next.catch(() => undefined)
    return next
  }
  const safeReport = (event: WritePlaneEvent): void => {
    try {
      report?.(event)
    } catch {
      // Telemetry never stands in front of a restart.
    }
  }
  /**
   * The pin, if it is THIS organization's. A pin for another organization (the user switched, and
   * `getInitialData` has not re-pinned yet) says nothing about this one: the write plane is deferred
   * and the memory is not lowered on its say-so.
   */
  const ownPin = (): SessionWriteModePin | null => {
    const pin = readSessionPin()
    return pin && pin.decision.organizationId === organizationId ? pin : null
  }
  /** A drain's target from a decision: its own `remember`; a server-confirmed non-Firestore write target clears it. */
  const drainTargetOf = (decision: WriteCutoverDecision, previous: WritesMaster | null): WritesMaster | null => {
    if (decision.remember && decision.remember.when === 'after-v2-empty') return decision.remember.value
    if (decision.to.writeMode !== null) return null
    return previous
  }

  return {
    observe: observeOrganizationFlags,
    decide: (read, observation) => {
      const pin = ownPin()
      return decideOrgCutoverTransition({
        ...read,
        observation,
        organizationId,
        session: sessionWritePlaneOf(pin),
        lastKnown: lastKnownOf(pin, watcherRemembered),
        capability,
      })
    },
    afterObservation: (observation, decision) => {
      const alerts = orgFlagsAlerts(observation)
      const key = alerts.join(',')
      if (key !== lastAlerts) {
        lastAlerts = key
        if (alerts.length > 0) {
          logger.error('📊 [write-cutover] the organization write flag is not in a state a device can honour', { organizationId, alerts })
          safeReport({ type: 'org-flags-alert', alerts })
        }
      }
      // A transition with `remember: 'now'` writes it here too: same rule, same value.
      const pin = ownPin()
      const value = memoryWriteFor(observation.writes, sessionWritePlaneOf(pin), lastKnownOf(pin, watcherIntended))
      if (!value || failedWrites.has(value)) return
      // A drain-to-empty's value is the drain's to write, once v2 is empty (memoryWriteFor already
      // refuses 'firestore' outside a Firestore-mode session; this keeps the two rules from drifting).
      if (isWriteCutoverDecision(decision) && decision.remember?.when === 'after-v2-empty') return
      void write(value)
    },
    start: (decision) => {
      safeReport({ type: 'transition-started', decision })
      if (decision.sequence === 'capped') return startWriteCutover(decision)
      drainTarget = drainTargetOf(decision, null)
      return startV2DrainThenRestart(decision, {
        rememberDrained: () => (drainTarget ? write(drainTarget) : Promise.resolve(false)),
        lowersMemory: () => drainTarget !== null,
        rememberUndrained: () => write('supabase'),
      })
    },
    retarget: (decision) => {
      const next = drainTargetOf(decision, drainTarget)
      if (next === drainTarget) return
      drainTarget = next
      safeReport({ type: 'transition-retargeted', decision, remember: next })
    },
    cancelled: (key, why) => safeReport({ type: 'transition-cancelled', key, why }),
  }
}
